import React, { Component, ReactNode, ErrorInfo } from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import { env } from '../config/env';
import { logger } from '../utils/logger';

interface AuthProviderProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for Clerk authentication
 * Allows the app to render even if Clerk fails to initialize
 */
class ClerkErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Clerk initialization error:', error, errorInfo);
    logger.warn('App will continue without authentication features');
  }

  override render() {
    if (this.state.hasError) {
      logger.warn('Clerk failed to load - authentication disabled');
      // Render children without Clerk if it fails
      return this.props.children;
    }

    return this.props.children;
  }
}

/**
 * Clerk Authentication Provider
 * Wraps the app with Clerk for authentication and user management
 * Falls back gracefully if Clerk fails to initialize
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return (
    <ClerkErrorBoundary>
      <ClerkProvider
        publishableKey={env.CLERK_PUBLISHABLE_KEY}
        clerkJSUrl="https://clerk.onarrivalglobal.com/npm/@clerk/clerk-js@5/dist/clerk.browser.js"
      >
        {children}
      </ClerkProvider>
    </ClerkErrorBoundary>
  );
};
